import {FrmVar} from "../../drawfrm/FrmVar";
import {FrmVarEvent} from "../../drawfrm/FrmVar";
import {inches} from "../../const";
import {serFrmVar, restoreFrmVar} from "../../serialize/frmVar";
import {SerDoorLite} from "../../../../../../api/elevationTool/DoorElevationData";
import {deGlass, MdGlass, serGlass} from "../glass/MdGlass";

// which of a lite's six dimensions is being edited. horizontal axis:
// offsetLeft + width + offsetRight = leafWidth. vertical axis:
// offsetTop + height + offsetBottom = leafHeight.
export type MdDoorLiteField =
    | "offsetLeft" | "width" | "offsetRight"
    | "offsetTop" | "height" | "offsetBottom";

// the three slots on one axis. `a` is the leading offset (left / top), `size` is
// width / height, `b` is the trailing offset (right / bottom).
type AxisKey = "a" | "size" | "b";

// A glass lite (window) cut into a door leaf. Positioned by four edge offsets
// plus its own width/height. On each axis the three values are over-determined
// against the leaf size (offsetLeft + width + offsetRight = leafWidth;
// offsetTop + height + offsetBottom = leafHeight), so editing one flexes the
// others — see editDimension() / the axis solver below.
export class MdDoorLite {
    id: string;

    // which leaf of a pair this lite belongs to. Single-leaf openings always use
    // "active". Set by MdDoorSpec.addLite.
    leaf: "active" | "inactive" = "active";

    // the reference mark drawn on the lite ("G1", "G2", …). Assigned by MdDoorSpec and
    // stable for the life of the lite, so anything that cites a lite by mark keeps
    // pointing at the same one. Empty only between construction and that assignment.
    mark: string = "";

    offsetTop: FrmVar;
    offsetLeft: FrmVar;
    offsetRight: FrmVar;
    offsetBottom: FrmVar;
    width: FrmVar;
    height: FrmVar;

    // undefined = the glass defaults. Read through doorLiteGlass(); set through
    // MdDoorSpec.setLiteGlass so the leaf's bead rule holds.
    glass?: MdGlass;

    constructor(id: string) {
        this.id = id;
        this.offsetTop = new FrmVar(`doorLite[${id}].offsetTop`, 0);
        this.offsetLeft = new FrmVar(`doorLite[${id}].offsetLeft`, 0);
        this.offsetRight = new FrmVar(`doorLite[${id}].offsetRight`, 0);
        this.offsetBottom = new FrmVar(`doorLite[${id}].offsetBottom`, 0);
        this.width = new FrmVar(`doorLite[${id}].width`, 12 * inches);
        this.height = new FrmVar(`doorLite[${id}].height`, 12 * inches);
    }

    toData(): SerDoorLite {
        return {
            id: this.id,
            mark: this.mark,
            leaf: this.leaf,
            offsetTop: serFrmVar(this.offsetTop),
            offsetLeft: serFrmVar(this.offsetLeft),
            offsetRight: serFrmVar(this.offsetRight),
            offsetBottom: serFrmVar(this.offsetBottom),
            width: serFrmVar(this.width),
            height: serFrmVar(this.height),
            glass: serGlass(this.glass),
        };
    }

    static fromData(s: SerDoorLite): MdDoorLite {
        const lite = new MdDoorLite(s.id);
        lite.mark = s.mark || "";
        lite.leaf = s.leaf;
        restoreFrmVar(lite.offsetTop, s.offsetTop);
        restoreFrmVar(lite.offsetLeft, s.offsetLeft);
        restoreFrmVar(lite.offsetRight, s.offsetRight);
        restoreFrmVar(lite.offsetBottom, s.offsetBottom);
        restoreFrmVar(lite.width, s.width);
        restoreFrmVar(lite.height, s.height);
        lite.glass = deGlass(s.glass);
        return lite;
    }

    // the FrmVar backing a field name (public: the drawing layer reads its value
    // and modified state to render + edit the dimension).
    varFor(f: MdDoorLiteField): FrmVar {
        return this.field(f);
    }

    // resolve the FrmVar backing a field name.
    private field(f: MdDoorLiteField): FrmVar {
        switch(f) {
            case "offsetLeft": return this.offsetLeft;
            case "width": return this.width;
            case "offsetRight": return this.offsetRight;
            case "offsetTop": return this.offsetTop;
            case "height": return this.height;
            case "offsetBottom": return this.offsetBottom;
        }
    }

    // the axis a field lives on, and which slot it occupies.
    private axisOf(f: MdDoorLiteField): {horizontal: boolean; key: AxisKey} {
        switch(f) {
            case "offsetLeft": return {horizontal: true, key: "a"};
            case "width": return {horizontal: true, key: "size"};
            case "offsetRight": return {horizontal: true, key: "b"};
            case "offsetTop": return {horizontal: false, key: "a"};
            case "height": return {horizontal: false, key: "size"};
            case "offsetBottom": return {horizontal: false, key: "b"};
        }
    }

    private axisVars(horizontal: boolean): Record<AxisKey, FrmVar> {
        return horizontal
            ? {a: this.offsetLeft, size: this.width, b: this.offsetRight}
            : {a: this.offsetTop, size: this.height, b: this.offsetBottom};
    }

    // center the lite in the leaf at the given size. all six vars are set (not
    // userSet) so none reads as user-modified — a freshly added lite has no
    // locked dimensions, letting later edits flex freely.
    centerIn(leafWidth: number, leafHeight: number, size = 12 * inches) {
        const ev: FrmVarEvent = {debug: "doorLite.center"};
        this.width.set(Math.min(size, leafWidth * 0.6), ev);
        this.height.set(Math.min(size, leafHeight * 0.6), ev);
        this.offsetLeft.set((leafWidth - this.width.get()) / 2, ev);
        this.offsetRight.set((leafWidth - this.width.get()) / 2, ev);
        this.offsetTop.set((leafHeight - this.height.get()) / 2, ev);
        this.offsetBottom.set((leafHeight - this.height.get()) / 2, ev);
    }

    // reposition the lite by its top-left within the leaf, holding width/height
    // and keeping both axes spanning the leaf. The top-left is clamped so the
    // rectangle stays fully inside the leaf. Offsets are set (not user-locked): a
    // drag is a soft reposition that later dimension edits can still flex around.
    moveTo(left: number, top: number, leafWidth: number, leafHeight: number) {
        const w = this.width.get();
        const h = this.height.get();
        const clampedLeft = clamp(left, 0, Math.max(0, leafWidth - w));
        const clampedTop = clamp(top, 0, Math.max(0, leafHeight - h));

        const ev: FrmVarEvent = {debug: "doorLite.move"};
        this.offsetLeft.set(clampedLeft, ev);
        this.offsetRight.set(Math.max(0, leafWidth - w - clampedLeft), ev);
        this.offsetTop.set(clampedTop, ev);
        this.offsetBottom.set(Math.max(0, leafHeight - h - clampedTop), ev);
    }

    // set the lite's rectangle from explicit leaf-local edges. Writes width/height
    // and all four offsets so both axes span the leaf. Used by edge-drag resize,
    // which clamps the edges against the leaf + a minimum size before calling.
    // Values are set (not user-locked), consistent with drag reposition.
    setRect(left: number, top: number, right: number, bottom: number, leafWidth: number, leafHeight: number) {
        const ev: FrmVarEvent = {debug: "doorLite.resize"};
        this.offsetLeft.set(left, ev);
        this.width.set(right - left, ev);
        this.offsetRight.set(leafWidth - right, ev);
        this.offsetTop.set(top, ev);
        this.height.set(bottom - top, ev);
        this.offsetBottom.set(leafHeight - bottom, ev);
    }

    // apply a user edit to one dimension, then flex the other two on that axis so
    // the axis still spans the leaf. The edited field is pinned (userSet marks it
    // modified); the solver picks which of the remaining two absorbs the slack.
    editDimension(f: MdDoorLiteField, value: number, leafWidth: number, leafHeight: number) {
        const {horizontal, key} = this.axisOf(f);
        this.field(f).userSet(value, {debug: `doorLite.edit.${f}`});
        // at most two of an axis's three dimensions may be locked — the third is derived. If this edit
        // locks all three, drop the lowest-priority lock so top-left + size win (see enforceMaxTwoLocked).
        this.enforceMaxTwoLocked(horizontal);
        this.resolveAxis(horizontal, horizontal ? leafWidth : leafHeight);
    }

    // keep at most two locked (user-modified) dimensions on an axis. When all three are locked the axis
    // is over-determined, so the trailing offset (right / bottom) — the lowest priority — is unlocked and
    // becomes the derived slot. This encodes the rule: locking e.g. height + top + bottom auto-unlocks
    // bottom (and width + left + right auto-unlocks right).
    private enforceMaxTwoLocked(horizontal: boolean) {
        const vars = this.axisVars(horizontal);
        const allLocked = (["a", "size", "b"] as AxisKey[]).every(k => vars[k].isModified());
        if(!allLocked) return;
        vars.b.clearModified();
    }

    // lock the two dimensions that pin the lite to the given edge — the single-lite "align" action
    // anchors it to that edge rather than aligning it to others. left → offsetLeft + width;
    // right → offsetRight + width; top → offsetTop + height; bottom → offsetBottom + height. The
    // opposite offset is unlocked so it derives (keeping at most two locked per axis).
    lockToEdge(edge: "left" | "right" | "top" | "bottom", leafWidth: number, leafHeight: number) {
        const horizontal = edge === "left" || edge === "right";
        const leading = edge === "left" || edge === "top"; // leading offset (a) vs trailing (b)
        const vars = this.axisVars(horizontal);

        vars.size.markModified();
        if(leading) {
            vars.a.markModified();
            vars.b.clearModified();
        } else {
            vars.b.markModified();
            vars.a.clearModified();
        }

        // re-derive the now-unlocked offset so the axis still spans the leaf.
        this.resolveAxis(horizontal, horizontal ? leafWidth : leafHeight);
    }

    // re-satisfy both axes against the current leaf size without any user edit
    // (e.g. the leaf resized). Non-user (unlocked) vars absorb the change, so locked
    // dimensions are preserved. Called before rendering so locked offsets hold on a
    // door of a different size than the design's.
    normalize(leafWidth: number, leafHeight: number) {
        this.resolveAxis(true, leafWidth);
        this.resolveAxis(false, leafHeight);
    }

    // make a + size + b == total by moving exactly one var (the "flex" slot), holding the others.
    //
    // flex preference: the derived (unlocked) slot, chosen in the order trailing offset (right/bottom),
    // then leading offset (left/top), then size (width/height). Because the pick prefers a non-locked
    // var, every locked dimension is held — so a locked dimension takes priority when the leaf resizes,
    // and with nothing locked the trailing offset gives while top-left + size hold. editDimension keeps
    // at most two locked per axis, so there is always an unlocked slot to flex; the fallback (all three
    // locked — only reachable from legacy data via normalize) flexes an offset, never size.
    private resolveAxis(horizontal: boolean, total: number) {
        const vars = this.axisVars(horizontal);
        const order: AxisKey[] = ["b", "a", "size"];

        // prefer a non-locked var; fall back to a locked offset but never size.
        let flex = order.find(k => !vars[k].isModified());
        if(!flex) flex = order.find(k => k !== "size")!;

        let fixed = 0;
        for(const k of ["a", "size", "b"] as AxisKey[]) {
            if(k !== flex) fixed += vars[k].get();
        }

        // clamp at 0: if the fixed slots already exceed the leaf, the lite
        // overflows its edge — a visible signal of an over-constrained layout.
        const next = Math.max(0, total - fixed);
        vars[flex].set(next, {debug: `doorLite.flex.${flex}`});
    }
}

function clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
}
